/**
 * Convert a value to a number safely.
 * If the value is empty, not a number, or something like Infinity,
 * it returns the defaultValue instead of crashing the slider.
 */
function safeParseFloat(value, defaultValue = 0) {
  const num = parseFloat(value || defaultValue);
  // isFinite returns false for NaN and Infinity, so we fall back to the default
  return Number.isFinite(num) ? num : defaultValue;
}

/**
 * Keep a value within a min and max boundary.
 * For example: clampValue(150, 0, 100) returns 100 because 150 is too high.
 * For example: clampValue(-5, 0, 100) returns 0 because -5 is too low.
 */
function clampValue(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Tell the page that a filter value has changed so the product list updates.
 * The theme listens for 'input' events on filter elements and re-fetches
 * the product list automatically when one is detected.
 */
function submitFormViaEvent(targetElement) {
  targetElement.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Run a setup function once on every matching range container on the page.
 * We mark each container after setting it up so that if this runs again
 * (e.g. after an AJAX filter update) we don't set up the same container twice.
 */
function forEachUninitializedRange(selector, callback) {
  document.querySelectorAll(selector).forEach(container => {
    if (container.dataset.rangeFacetInit) return;
    container.dataset.rangeFacetInit = '1';
    callback(container);
  });
}

/**
 * Color the slider track between the two thumbs.
 * The track uses a CSS gradient controlled by two CSS variables:
 * --range-start: where the colored section begins (as a %)
 * --range-end:   where the colored section ends (as a %)
 * JavaScript calculates these percentages based on the current slider values.
 */
function updateTrackFill(trackWrap, minVal, maxVal, globalMin, globalMax) {
  if (!trackWrap) return;

  const rangeSize = globalMax - globalMin;

  if (rangeSize <= 0) {
    trackWrap.style.setProperty('--range-start', '0%');
    trackWrap.style.setProperty('--range-end', '100%');
    return;
  }

  const startPercent = Math.max(0, Math.min(100, ((Math.min(minVal, maxVal) - globalMin) / rangeSize) * 100));
  const endPercent = Math.max(0, Math.min(100, ((Math.max(minVal, maxVal) - globalMin) / rangeSize) * 100));

  trackWrap.style.setProperty('--range-start', `${startPercent}%`);
  trackWrap.style.setProperty('--range-end', `${endPercent}%`);
}

/**
 * Let users click or drag anywhere on the slider track (not just the thumb circles).
 * When the user clicks, we figure out which thumb (min or max) is closest to
 * the click position and move that one. While dragging, we keep moving it.
 */
function attachTrackPointerHandlers(trackWrap, minRange, maxRange, globalMin, globalMax) {
  if (!trackWrap) return;

  let activeThumb = null;

  function getValueFromClientX(clientX) {
    const rect = trackWrap.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return globalMin + percentage * (globalMax - globalMin);
  }

  function updateActiveThumb(clientX) {
    if (!activeThumb) return;
    activeThumb.value = getValueFromClientX(clientX);
    activeThumb.dispatchEvent(new Event('input', { bubbles: true }));
  }

  trackWrap.addEventListener('pointerdown', event => {
    event.preventDefault();

    const clickedValue = getValueFromClientX(event.clientX);
    const minValue = safeParseFloat(minRange.value);
    const maxValue = safeParseFloat(maxRange.value);
    const distToMin = Math.abs(clickedValue - minValue);
    const distToMax = Math.abs(clickedValue - maxValue);

    activeThumb = distToMin <= distToMax ? minRange : maxRange;
    updateActiveThumb(event.clientX);
    trackWrap.setPointerCapture(event.pointerId);
  });

  trackWrap.addEventListener('pointermove', event => updateActiveThumb(event.clientX));
  trackWrap.addEventListener('pointerup', event => {
    if (!activeThumb) return;
    activeThumb.dispatchEvent(new Event('change', { bubbles: true }));
    activeThumb = null;
    trackWrap.releasePointerCapture(event.pointerId);
  });
  trackWrap.addEventListener('pointercancel', () => {
    activeThumb = null;
  });
}

/**
 * Keep the two range sliders and two number inputs in sync with each other.
 * When the user drags a slider, the matching number input updates.
 * When the user types in a number input, the matching slider updates.
 * The optional onChangeCallback runs after every change — used to update
 * the track color and enable/disable the hidden option inputs.
 */
function attachRangeSync(minRangeInput, maxRangeInput, minNumberInput, maxNumberInput, globalMin, globalMax, onChangeCallback = null) {
  // When a slider moves, update the number inputs to match.
  // skipCallback=true is used on first load so we don't trigger a filter update.
  function syncRangeToNumbers(activeInput, skipCallback = false) {
    let minVal = safeParseFloat(minRangeInput.value);
    let maxVal = safeParseFloat(maxRangeInput.value);

    if (minVal > maxVal) {
      if (activeInput === minRangeInput) { maxVal = minVal; maxRangeInput.value = maxVal; }
      else { minVal = maxVal; minRangeInput.value = minVal; }
    }

    // Only update a number input if the user isn't currently typing in it,
    // otherwise we would interrupt them mid-type.
    if (document.activeElement !== minNumberInput) minNumberInput.value = minVal;
    if (document.activeElement !== maxNumberInput) maxNumberInput.value = maxVal;

    if (!skipCallback && onChangeCallback) onChangeCallback(minVal, maxVal);
  }

  // When a number input changes, update the sliders to match.
  // While the user is still typing (finalize=false), we only move the slider.
  // When the user finishes typing and leaves the field (finalize=true), we also
  // clamp the typed value to the allowed range and write it back to the input.
  function syncNumbersToRange(finalize = false) {
    let minVal = clampValue(safeParseFloat(minNumberInput.value), globalMin, globalMax);
    let maxVal = clampValue(safeParseFloat(maxNumberInput.value), globalMin, globalMax);

    if (finalize) {
      if (minVal > maxVal) {
        if (document.activeElement === minNumberInput) maxVal = minVal;
        else minVal = maxVal;
      }
      minNumberInput.value = minVal;
      maxNumberInput.value = maxVal;
    }

    minRangeInput.value = minVal;
    maxRangeInput.value = maxVal;
    if (onChangeCallback) onChangeCallback(minVal, maxVal);
  }

  // While dragging a slider, keep the number inputs updated.
  // When the user releases the slider, also trigger a filter/product update.
  [minRangeInput, maxRangeInput].forEach(input => {
    input.addEventListener('input', () => syncRangeToNumbers(input));
    input.addEventListener('change', () => { syncRangeToNumbers(input); submitFormViaEvent(input); });
  });

  // Set up a number input so that typing updates the slider live,
  // but only triggers a product list update when the user is done.
  // The 'interacted' flag prevents a filter update firing when the page
  // sets the value programmatically (e.g. on first load).
  function attachNumberInput(numberInput) {
    let interacted = false;

    numberInput.addEventListener('input', () => { interacted = true; syncNumbersToRange(); });
    numberInput.addEventListener('change', () => {
      if (!interacted) return;
      interacted = false;
      syncNumbersToRange(true);
      submitFormViaEvent(numberInput);
    });
    numberInput.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      interacted = false;
      syncNumbersToRange(true);
      numberInput.blur();
      submitFormViaEvent(numberInput);
    });
  }

  attachNumberInput(minNumberInput);
  attachNumberInput(maxNumberInput);

  // Set the initial slider and number input positions based on the server-rendered values.
  // We skip the callback here so the hidden option inputs stay exactly as
  // the server rendered them — they should only change when the user interacts.
  syncRangeToNumbers(minRangeInput, true);
}

/**
 * Find all range filter sliders on the page and wire them up.
 * There are two types:
 * 1. Simple numeric ranges (e.g. price) — these use hidden inputs that the form submits directly.
 * 2. List-based ranges (e.g. power, capacity) — these use hidden checkboxes that get enabled
 *    or disabled based on the selected range, because Shopify Search & Discovery
 *    exposes these metafield values as a list of discrete options, not a true range.
 * This function also runs again after AJAX filter updates so new HTML gets wired up too.
 */
function initRangeFacets() {
  // Handle simple price/numeric range filters
  forEachUninitializedRange('.numeric-range', container => {
    const minRange = container.querySelector('.numeric-range__input--min');
    const maxRange = container.querySelector('.numeric-range__input--max');
    const minNumber = container.querySelector('.numeric-range__number--min');
    const maxNumber = container.querySelector('.numeric-range__number--max');
    const hiddenMin = container.querySelector('.numeric-range__hidden-min');
    const hiddenMax = container.querySelector('.numeric-range__hidden-max');

    if (!minRange || !maxRange || !minNumber || !maxNumber) return;

    // Each time the slider or number input changes, write the current min/max
    // into the hidden inputs so the form picks them up on submit.
    attachRangeSync(
      minRange,
      maxRange,
      minNumber,
      maxNumber,
      safeParseFloat(minRange.getAttribute('min')),
      safeParseFloat(maxRange.getAttribute('max')),
      (minVal, maxVal) => {
        if (hiddenMin) hiddenMin.value = minVal;
        if (hiddenMax) hiddenMax.value = maxVal;
      }
    );
  });

  // Handle list-based range filters (metafields exposed as discrete values by Search & Discovery)
  forEachUninitializedRange('.ecostal-list-range', container => {
    const minRange = container.querySelector('.ecostal-list-range__range--min');
    const maxRange = container.querySelector('.ecostal-list-range__range--max');
    const minNumber = container.querySelector('.ecostal-list-range__number--min');
    const maxNumber = container.querySelector('.ecostal-list-range__number--max');
    const trackWrap = container.querySelector('.ecostal-list-range__track-wrap');
    const optionInputs = Array.from(container.querySelectorAll('[data-range-option]'));

    if (!minRange || !maxRange || !minNumber || !maxNumber || optionInputs.length === 0) return;

    const globalMin = safeParseFloat(container.dataset.min);
    const globalMax = safeParseFloat(container.dataset.max);

    /**
     * Enable or disable the hidden inputs based on the current slider range.
     * Each option value has a hidden input. Only the ones within the selected
     * min/max range are enabled — disabled inputs are ignored by the browser
     * when the form submits, so only the selected values reach Shopify.
     */
    function applyCheckboxSelection() {
      const minVal = safeParseFloat(minRange.value);
      const maxVal = safeParseFloat(maxRange.value);

      optionInputs.forEach(input => {
        const optionValue = safeParseFloat(input.dataset.rangeOptionValue);
        input.disabled = !(optionValue >= minVal && optionValue <= maxVal);
      });
    }

    // Use the shared sync function, but also update checkbox and track on change
    attachRangeSync(
      minRange,
      maxRange,
      minNumber,
      maxNumber,
      globalMin,
      globalMax,
      () => {
        updateTrackFill(trackWrap, safeParseFloat(minRange.value), safeParseFloat(maxRange.value), globalMin, globalMax);
        applyCheckboxSelection();
      }
    );

    // Draw the initial colored section of the track based on the server-rendered values.
    // We do NOT call applyCheckboxSelection here — the server already rendered the
    // hidden option inputs in the correct enabled/disabled state, so we leave them
    // alone until the user actually moves the slider.
    updateTrackFill(trackWrap, safeParseFloat(minRange.value), safeParseFloat(maxRange.value), globalMin, globalMax);
    attachTrackPointerHandlers(trackWrap, minRange, maxRange, globalMin, globalMax);
  });
}

// Run once when the page first loads, and again every time the filter
// results update via AJAX (the theme fires 'facetFiltersUpdated' after each update).
document.addEventListener('DOMContentLoaded', initRangeFacets);
document.addEventListener('facetFiltersUpdated', initRangeFacets);